import * as Dfns from 'date-fns/fp';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Bar, ComposedChart, Line, Tooltip as RechartsTooltip } from 'recharts';
import { ChartXAxis, ChartYAxis, ChartGrid } from '@Components/Charts/chart-presets';
import { LINE_STROKE_WIDTH, X_AXIS_PADDING } from '@Components/Charts/ChartConstants';
import { ChartTooltipV2, ColorKey, KeyShape } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { PacingBarColors } from '@src/utils/colors';

export interface StreamingPacingChartRow {
  date: string;
  ideal: number;
  value: number;
  sum?: number;
  idealSum?: number;
}

const SERIES: Record<string, { label: string; color: string; shape: KeyShape }> = {
  value: {
    label: 'Actual Daily Delivery',
    color: PacingBarColors.deliveryUnsecured,
    shape: 'square'
  },
  ideal: { label: 'Ideal Daily Delivery', color: PacingBarColors.pacing, shape: 'dashedLine' },
  sum: { label: 'Actual Delivery', color: PacingBarColors.deliverySecured, shape: 'line' },
  idealSum: { label: 'Ideal Delivery', color: PacingBarColors.booked, shape: 'dashedLine' }
};
const BAR_SIZE = 20;
const DASHED = '4';

interface ChartTooltipContentProps {
  active?: boolean;
  label?: string | number;
  payload?: { name?: string; value?: number | string; color?: string }[];
  formatNumber: (value: number) => string;
}

const ChartTooltipContent = ({
  active,
  label,
  payload,
  formatNumber
}: ChartTooltipContentProps) => {
  if (!active || !payload) {
    return null;
  }
  return (
    <ChartTooltipV2
      headers={[Dfns.format('M/d/yyyy', Dfns.parseISO(String(label)))]}
      itemKeyShape='square'
      items={payload.map(({ name, value, color }) => ({
        label: name,
        value: formatNumber(Number(value)),
        color
      }))}
    />
  );
};

interface StreamingPacingChartProps {
  data: StreamingPacingChartRow[];
  booked?: number;
  title: string;
  subtitle: string;
  formatNumber: (value: number) => string;
}

export const StreamingPacingChart = ({
  data,
  booked,
  title,
  subtitle,
  formatNumber
}: StreamingPacingChartProps) => (
  <>
    <div className='streaming-pacing-chart-header'>
      <div>
        <div className='streaming-pacing-chart-title'>{title}</div>
        <div className='streaming-pacing-chart-subtitle'>{subtitle}</div>
      </div>
      <div className='streaming-pacing-chart-legend'>
        {Object.entries(SERIES).map(([key, { label, color, shape }]) => (
          <div key={key} className='streaming-pacing-legend-group'>
            <ColorKey shape={shape} color={color} />
            {label}
          </div>
        ))}
      </div>
    </div>
    <div className='streaming-pacing-chart'>
      <AutoSizer
        renderProp={({ width = 0, height = 0 }) => (
          <ComposedChart
            data={data}
            width={width}
            height={height}
            // Resizing makes recharts drop the x axis, so force a full rerender.
            key={`${width}_${height}`}
          >
            <ChartGrid vertical={false} />
            <ChartXAxis
              dataKey='date'
              padding={X_AXIS_PADDING}
              tickFormatter={date => Dfns.format('M/dd', Dfns.parseISO(date))}
            />
            <ChartYAxis
              type='number'
              domain={booked ? [0, booked] : undefined}
              tickFormatter={formatNumber}
            />
            <Bar
              name={SERIES.value.label}
              dataKey='value'
              barSize={BAR_SIZE}
              fill={SERIES.value.color}
            />
            <Line
              name={SERIES.ideal.label}
              dataKey='ideal'
              stroke={SERIES.ideal.color}
              strokeWidth={LINE_STROKE_WIDTH}
              strokeDasharray={DASHED}
            />
            <Line
              name={SERIES.sum.label}
              type='monotoneX'
              dataKey='sum'
              stroke={SERIES.sum.color}
              strokeWidth={LINE_STROKE_WIDTH}
            />
            <Line
              name={SERIES.idealSum.label}
              dataKey='idealSum'
              stroke={SERIES.idealSum.color}
              strokeWidth={LINE_STROKE_WIDTH}
              strokeDasharray={DASHED}
            />
            <RechartsTooltip content={<ChartTooltipContent formatNumber={formatNumber} />} />
          </ComposedChart>
        )}
      />
    </div>
  </>
);
